import { defineComponent as _defineComponent } from 'vue'

import Breadcrumb from '@/components/Breadcrumb/Breadcrumb.vue'
import BreadcrumbItem from '@/components/Breadcrumb/BreadcrumbItem.vue'
import ChecklistStepPanel from './ChecklistStepPanel.vue'

const __default__ = {
  name: 'active-checklist',
  components: {
    Breadcrumb,
    BreadcrumbItem,
    ChecklistStepPanel
  }
}

import { ref, reactive, computed, watch, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { formatGroupDate, formatGroupTime } from '@/plugins/dates'
import { htmlToPlainText } from '@/util/richText'
import { formattedDate, time } from '@/plugins/dateFormatter'


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const group = JSON.parse(localStorage.getItem('group') || '{}')
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${group.id}`

const loading = ref(true)
const completing = ref(false)
const saveError = ref(false)
const run = ref<any>({})
const steps = ref<any[]>([])
const invalidStepIds = ref<number[]>([])
const selectedId = ref<number | null>(null)
const drawerOpen = ref(false)
const isWide = ref(true)
const alert = reactive({ show: false, title: '', message: '', variant: 'warning', discard: false, redirect: '' })
const catalog = ref<any[]>([])
const tagsSaving = reactive<Record<string, boolean>>({ run: false })

const completedCount = computed(() => steps.value.filter(s => s.is_completed === 1).length)
const issueCount = computed(() => steps.value.filter(s => s.issue_flagged === 1).length)
const progressPercent = computed(() => steps.value.length ? Math.round((completedCount.value / steps.value.length) * 100) : 0)
const selectedIndex = computed(() => steps.value.findIndex(s => s.id === selectedId.value))
const selectedStep = computed(() => selectedIndex.value >= 0 ? steps.value[selectedIndex.value] : null)

const SAVE_FIELDS = ['id', 'is_completed', 'is_skipped', 'response_value', 'note', 'issue_flagged', 'issue_description', 'photo_urls']
const timers = new Map<number, any>()
// Steps with typed text that has not been saved yet. Free text saves when the
// runner leaves the field, never mid-typing, so the box is never rewritten
// under the caret.
const dirty = new Set<number>()
const inflight = new Set<Promise<void>>()
const mql = window.matchMedia('(min-width: 992px)')
function onMedia() { isWide.value = mql.matches }

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

function parseError(err: any): string {
  const data = err?.response?.data
  if (!data) return 'Something went wrong'
  if (typeof data === 'string') {
    try { return JSON.parse(data).error || data } catch (e) { return data }
  }
  return data.error || 'Something went wrong'
}

function showAlert(title: string, message: string, variant = 'warning', redirect = '') {
  alert.title = title
  alert.message = message
  alert.variant = variant
  alert.redirect = redirect
  alert.show = true
}
function onAlertConfirm() {
  alert.show = false
  if (alert.redirect) proxy.$router.push(alert.redirect)
}
function onPanelError(message: string) {
  showAlert('Upload failed', message, 'danger')
}

function handleError(err: any) {
  const status = err?.response?.status
  if (status === 401 || status === 403) {
    proxy.$router.push('/forbidden')
    return
  }
  if (status === 404) {
    showAlert('Run not found', 'This run no longer exists.', 'warning', '/checklists/new')
    return
  }
  showAlert('Error', parseError(err), 'danger')
}

function sessionDate(d: string) { return formattedDate(group.date_format, d) }
function sessionTime(t: string) { return time(group.time_format, t) }

function needsResponse(step: any): boolean {
  const t = step.response_type || 'checkbox'
  if (t === 'checkbox') return false
  const v = step.response_value
  return v === null || v === undefined || String(v).trim() === '' || String(v).trim() === '[]'
}
const invalidSummary = computed(() => {
  const rows = steps.value.filter(s => invalidStepIds.value.includes(s.id))
  const unanswered = rows.filter(s => needsResponse(s)).length
  const undone = rows.length - unanswered
  const parts: string[] = []
  if (undone > 0) parts.push(`${undone} required step${undone > 1 ? 's are' : ' is'} not done`)
  if (unanswered > 0) parts.push(`${unanswered} required step${unanswered > 1 ? 's need' : ' needs'} a response`)
  return parts.join(' and ') + ' before this checklist can be completed.'
})
function responseSummary(step: any): string {
  const t = step.response_type || 'checkbox'
  if (t === 'checkbox') return ''
  const v = step.response_value
  if (v === null || v === undefined || v === '') return ''
  if (t === 'multiselect') {
    try { const arr = JSON.parse(v); return Array.isArray(arr) ? arr.join(', ') : String(v) } catch (e) { return String(v) }
  }
  return String(v).length > 24 ? String(v).slice(0, 24) + '…' : String(v)
}

async function loadCatalog() {
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}/tags`, config)
    catalog.value = Array.isArray(response.data) ? response.data : []
  } catch (e) {
    catalog.value = []
  }
}

// Replace the tag set on the run or on one step. Optimistic revert on failure.
async function setTags(target: 'run' | 'step', step: any, ids: number[]) {
  const key = target === 'run' ? 'run' : 's' + step.id
  if (tagsSaving[key]) return
  tagsSaving[key] = true
  const holder = target === 'run' ? run.value : step
  const previous = holder.tags || []
  try {
    const config = await authConfig()
    const response = await axios.put(`${baseUrl}/checklists/instances`,
      { id: run.value.id, action: 'set_tags', target, step_id: target === 'step' ? step.id : undefined, tag_ids: ids }, config)
    holder.tags = response.data.tags || []
  } catch (err: any) {
    holder.tags = previous
    proxy.$notify({ type: 'danger', message: 'Failed to update tags', icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    tagsSaving[key] = false
  }
}
function addTag(target: 'run' | 'step', step: any, tagId: number) {
  const holder = target === 'run' ? run.value : step
  const current = (holder.tags || []).map((t: any) => t.id)
  if (current.includes(tagId)) return
  setTags(target, step, [...current, tagId])
}
function removeTag(target: 'run' | 'step', step: any, tag: any) {
  const holder = target === 'run' ? run.value : step
  setTags(target, step, (holder.tags || []).map((t: any) => t.id).filter((id: number) => id !== tag.id))
}

async function loadRun() {
  loading.value = true
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}/checklists/instances/${proxy.$route.query.run}`, config)
    run.value = response.data.run
    steps.value = response.data.steps
    if (run.value.status !== 'in_progress') {
      proxy.$router.push('/checklists/history/instance?id=' + run.value.id)
      return
    }
    const firstOpen = steps.value.find(s => s.is_completed !== 1) || steps.value[0]
    selectedId.value = firstOpen ? firstOpen.id : null
  } catch (err) {
    handleError(err)
  } finally {
    loading.value = false
  }
}

function selectStep(step: any) {
  selectedId.value = step.id
  if (!isWide.value) drawerOpen.value = true
}
// On a phone the row is the tick target and the chevron opens the step, so a
// runner working down the list never opens the drawer by accident. On a wide
// screen the panel is always visible, so a row tap just selects the step.
function onRowTap(step: any) {
  if (isWide.value) selectStep(step)
  else toggleStep(step)
}

function payloadFor(step: any) {
  const out: any = {}
  for (const f of SAVE_FIELDS) out[f] = step[f]
  if (step.response_type === 'multiselect') {
    const v = step.response_value
    if (Array.isArray(v)) out.response_value = v
    else { try { out.response_value = JSON.parse(v || '[]') } catch (e) { out.response_value = [] } }
  }
  return out
}

async function saveStep(step: any, attempt = 0) {
  try {
    const config = await authConfig()
    const response = await axios.put(`${baseUrl}/checklists/instances`,
      { id: run.value.id, action: 'save_steps', steps: [payloadFor(step)] }, config)
    const saved = (response.data.steps || [])[0]
    if (saved) {
      // Merge, not replace: the save response carries no tags or ticket link,
      // and those live on the same object. The fields the runner edits keep
      // the client's copy: the server trims text, and a response that lands
      // after newer edits would roll them back.
      const target = steps.value.find(s => s.id === saved.id)
      if (target) {
        for (const f of SAVE_FIELDS) delete saved[f]
        Object.assign(target, saved)
      }
    }
    saveError.value = false
    if (step.is_completed === 1) invalidStepIds.value = invalidStepIds.value.filter(id => id !== step.id)
  } catch (err: any) {
    const status = err?.response?.status
    if (status === 401 || status === 403 || status === 404 || status === 409) {
      handleError(err)
      return
    }
    if (attempt < 1) {
      await new Promise(resolve => setTimeout(resolve, 1000))
      await saveStep(step, attempt + 1)
      return
    }
    saveError.value = true
  }
}

// Tracks the save so completing the run can wait for it to land.
async function startSave(step: any) {
  const p = saveStep(step)
  inflight.add(p)
  try {
    await p
  } finally {
    inflight.delete(p)
  }
}

function queueSave(step: any, immediate = false) {
  const existing = timers.get(step.id)
  if (existing) clearTimeout(existing)
  dirty.delete(step.id)
  if (immediate) {
    startSave(step)
    return
  }
  timers.set(step.id, setTimeout(() => { timers.delete(step.id); startSave(step) }, 400))
}

function toggleStep(step: any) {
  // A step that asks for an answer is done when it has one. Refuse the tick
  // until then and open the step so the answer can be given.
  if (step.is_completed !== 1 && needsResponse(step)) {
    selectedId.value = step.id
    if (!isWide.value) drawerOpen.value = true
    proxy.$notify({ type: 'warning', message: 'Answer this step to mark it done', icon: 'tim-icons icon-alert-circle-exc' })
    return
  }
  step.is_completed = step.is_completed === 1 ? 0 : 1
  queueSave(step, true)
  // Ticking the step that is open in the panel moves the panel on to the next
  // unfinished step, so a runner can work top to bottom. Ticking another row
  // leaves the selection alone.
  if (step.is_completed === 1 && selectedId.value === step.id) advanceFrom(step)
}
function advanceFrom(step: any) {
  const idx = steps.value.findIndex(s => s.id === step.id)
  const after = steps.value.slice(idx + 1).find(s => s.is_completed !== 1)
  const before = steps.value.slice(0, idx).find(s => s.is_completed !== 1)
  const next = after || before
  if (next) selectedId.value = next.id
}
function onPanelToggle(step: any) {
  toggleStep(step)
}
function onDrawerToggle(step: any) {
  toggleStep(step)
  if (step.is_completed === 1) drawerOpen.value = false
}
// mode: 'typing' only records the value; 'blur' saves the typed text now.
function onField(step: any, field: string, value: any, mode?: 'typing' | 'blur') {
  if (field === 'response_value') {
    step.response_value = Array.isArray(value) ? JSON.stringify(value) : (value === undefined || value === null ? null : String(value))
    // A chosen option or entered number is the work itself: mark the step done.
    // Text steps wait for an explicit tick so typing is never cut short.
    const discrete = ['dropdown', 'multiselect', 'number'].includes(step.response_type)
    if (discrete && !needsResponse(step) && step.is_completed !== 1) {
      step.is_completed = 1
      queueSave(step, true)
      return
    }
    if (discrete && needsResponse(step) && step.is_completed === 1) {
      step.is_completed = 0
      queueSave(step, true)
      return
    }
  } else {
    step[field] = value
  }
  if (mode === 'typing') {
    dirty.add(step.id)
    return
  }
  const immediate = mode === 'blur' || field === 'issue_flagged' || field === 'photo_urls'
  queueSave(step, immediate)
}

// Saves everything queued or typed but unsaved, and waits for every save in
// flight, including ones started by a blur a moment earlier.
async function flushPending() {
  const ids = new Set<number>([...timers.keys(), ...dirty])
  for (const t of timers.values()) clearTimeout(t)
  timers.clear()
  dirty.clear()
  for (const id of ids) {
    const step = steps.value.find(s => s.id === id)
    if (step) startSave(step)
  }
  await Promise.all([...inflight])
}

// Moving to another step or closing the drawer counts as leaving the field,
// even where the browser does not blur it (iOS keeps focus on a tap outside).
watch(selectedId, () => { flushPending() })
watch(drawerOpen, open => { if (!open) flushPending() })
// Switching apps or locking the phone does not reliably blur the field either.
function onVisibility() {
  if (document.visibilityState === 'hidden') flushPending()
}

function saveAndExit() {
  flushPending()
  proxy.$router.push('/checklists/new')
}

async function completeRun() {
  completing.value = true
  try {
    // The last edit must land before the server checks required answers.
    await flushPending()
    const config = await authConfig()
    const res = await axios.put(`${baseUrl}/checklists/instances`, { id: run.value.id, action: 'complete' }, config)
    const status = res.data && res.data.status
    proxy.$notify({ type: 'success', message: status === 'awaiting_approval' ? 'Checklist sent for approval' : 'Checklist completed', icon: 'tim-icons icon-check-2' })
    proxy.$router.push('/checklists/new')
  } catch (err: any) {
    const data = err?.response?.data
    let parsed: any = data
    if (typeof data === 'string') { try { parsed = JSON.parse(data) } catch (e) { parsed = null } }
    if (err?.response?.status === 400 && parsed && Array.isArray(parsed.validation_errors)) {
      invalidStepIds.value = parsed.validation_errors.map((v: any) => v.step_instance_id)
      const first = steps.value.find(s => invalidStepIds.value.includes(s.id))
      if (first) {
        selectedId.value = first.id
        const el = document.getElementById('step-' + first.id)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      return
    }
    handleError(err)
  } finally {
    completing.value = false
  }
}

async function discardRun() {
  alert.discard = false
  loading.value = true
  try {
    const config = await authConfig()
    await axios.put(`${baseUrl}/checklists/instances`, { id: run.value.id, action: 'discard' }, config)
    proxy.$notify({ type: 'success', message: 'Run discarded', icon: 'tim-icons icon-check-2' })
    proxy.$router.push('/checklists/new')
  } catch (err) {
    loading.value = false
    handleError(err)
  }
}

onMounted(() => {
  onMedia()
  mql.addEventListener('change', onMedia)
  document.addEventListener('visibilitychange', onVisibility)
  loadCatalog()
  loadRun()
})
onBeforeUnmount(() => {
  mql.removeEventListener('change', onMedia)
  document.removeEventListener('visibilitychange', onVisibility)
  flushPending()
})

return { __sfc: true,proxy, companyId, group, baseUrl, loading, completing, saveError, run, steps, invalidStepIds, selectedId, drawerOpen, isWide, alert, catalog, tagsSaving, completedCount, issueCount, progressPercent, selectedIndex, selectedStep, SAVE_FIELDS, timers, dirty, inflight, mql, onMedia, authConfig, parseError, showAlert, onAlertConfirm, onPanelError, handleError, sessionDate, sessionTime, needsResponse, invalidSummary, responseSummary, loadCatalog, setTags, addTag, removeTag, loadRun, selectStep, onRowTap, payloadFor, saveStep, startSave, queueSave, toggleStep, advanceFrom, onPanelToggle, onDrawerToggle, onField, flushPending, onVisibility, saveAndExit, completeRun, discardRun, Breadcrumb, BreadcrumbItem, ChecklistStepPanel, formatGroupDate, formatGroupTime, htmlToPlainText }
}

})